import {Box} from '@mui/material';
import {t} from 'i18next';
import {RedactionPanelStatus} from '../../../../../api/types/document.type';
import RedactionNavigation from './RedactionNavigation';
import {Dispatch} from 'react';

interface RedactionNavigationWrapperProps {
  allRedactions: {[key: string]: Redaction};
  batchRedactions: {[key: string]: Redaction};
  noHitTerms: RedactionPanelStatus[];
  partialHitTerms: RedactionPanelStatus[];
  onBatchNavigation: (index: number, value: Redaction) => void;
  setShowDeleteBatchModal: Dispatch<React.SetStateAction<boolean>>;
  setBatchRedactionLabel: Dispatch<React.SetStateAction<string>>;
}

const RedactionNavigationWrapper: React.FC<RedactionNavigationWrapperProps> = ({
  allRedactions,
  batchRedactions,
  noHitTerms,
  partialHitTerms,
  onBatchNavigation,
  setShowDeleteBatchModal,
  setBatchRedactionLabel,
}) => {
  const hasBatchRedactions = Object.keys(batchRedactions).length > 0;
  const hasNoHits = noHitTerms.length > 0;
  const hasPartialHits = partialHitTerms.length > 0;

  if (!hasBatchRedactions && !hasNoHits && !hasPartialHits) {
    return null;
  }

  const renderRedactions = (
    redactions: {[key: string]: Redaction},
    tooltipContent: string,
    tooltipTitle: string,
    fullWidthDivider?: boolean
  ) =>
    Object.entries(redactions).map(([key, value], index, arr) => {
      const isLast = index === arr.length - 1 && !hasNoHits;
      const isPartial = partialHitTerms.some(
        (term) => term.term === value.term
      );
      return (
        <RedactionNavigation
          key={key}
          totalCount={value.count}
          onChange={(index: number) => onBatchNavigation(index, value)}
          tooltipTitle={
            isPartial ? t('docView.partialBatchRedactionErrors') : tooltipTitle
          }
          isPartial={isPartial}
          currentCount={value.currentCounter + 1}
          label={value.term}
          isDefault={value.isDefaultRedaction}
          tooltipContent={tooltipContent}
          fullWidthDivider={fullWidthDivider || isLast}
          setShowDeleteBatchModal={setShowDeleteBatchModal}
          setBatchRedactionLabel={setBatchRedactionLabel}
        />
      );
    });

  const renderStatusTerms = (
    terms: RedactionPanelStatus[],
    keyPrefix: string
  ) =>
    terms.map((term, index, arr) => (
      <RedactionNavigation
        key={`${keyPrefix}-${term.term}`}
        currentCount={term.actualCount}
        totalCount={term.expectedCount}
        label={term.term}
        onChange={() => {}}
        showNavigationButtons={false}
        tooltipTitle={t('docView.fullBatchRedactionErrors')}
        isFailed={true}
        fullWidthDivider={index === arr.length - 1}
      />
    ));

  return (
    <Box
      display="flex"
      flexDirection="column"
      data-testid="redaction-navigation-wrapper"
    >
      {allRedactions && (
        <Box
          display="flex"
          flexDirection="column"
          gap={1}
          data-testid="redaction-navigation-wrapper-all"
        >
          {renderRedactions(
            allRedactions,
            t('docView.allRedactionsTooltipText'),
            t('docView.allRedactions'),
            true
          )}
        </Box>
      )}
      {hasBatchRedactions && (
        <Box
          display="flex"
          flexDirection="column"
          gap={1}
          data-testid="redaction-navigation-wrapper-batch"
        >
          {renderRedactions(
            batchRedactions,
            t('docView.batchRedactionsTooltip'),
            t('docView.batchRedactions')
          )}
        </Box>
      )}

      {hasNoHits && (
        <Box
          display="flex"
          flexDirection="column"
          gap={1}
          data-testid="redaction-navigation-wrapper-nohits"
        >
          {renderStatusTerms(noHitTerms, 'no-hit')}
        </Box>
      )}
    </Box>
  );
};

export default RedactionNavigationWrapper;
